import { Router } from '@remix-run/router';
import { createCrossWindowChannel } from '@/core/deep-dive/routing/cross-window-channel';

// The dashboard's real BrowserRouter lives in a completely different router
// instance than the side panel (which renders its own MemoryRouter, see
// side-panel-shell.tsx) — there is no shared React Router context between
// them. Stashing a reference here lets code outside the dashboard's tree
// (e.g. a side-panel deep-dive widget) navigate the real dashboard URL via
// the data router's imperative `.navigate()`, without prop-drilling through
// two unrelated router trees.
let appRouter: Router | null = null;

export function setAppRouter(router: Router): void {
  appRouter = router;
}

// When the side panel is popped out into its own window (see
// deep-dive-window.ts), that window loads a full, separate App.tsx and
// gets its own `appRouter` singleton — a distinct JS realm from the main
// dashboard's. `window.opener` is only set on that popped-out window (same
// signal `isSidePanelInline` uses in deep-dive-window.ts), so it's used
// here to forward navigation intent back to the main window instead of
// navigating the popup itself.
interface NavigateMessage {
  path: string;
}
const navigateChannel = createCrossWindowChannel<NavigateMessage>('mni:app-navigate');

export function navigateApp(path: string): void {
  if (window.opener) {
    navigateChannel.publish({ path });
    return;
  }
  if (!appRouter) {
    window.location.assign(path);
    return;
  }
  appRouter.navigate(path);
}

navigateChannel.subscribe(({ path }) => {
  if (window.opener) return; // ignore in the popup itself
  if (appRouter) appRouter.navigate(path);
});
